import { searchTools } from './client'

//Fuzzy tool resolution for pages that don't match by URL (forum posts,
//blogs, Reddit threads about a tool). Raw titles are noisy —
//"Is Notion worth it? : r/SaaS - Reddit" — so a single title query takes
//whatever the search index happens to rank first. Instead we derive
//several candidate queries from the title, score every tool hit across
//them, and keep the runner-ups as `alternatives` so the UI can offer
//one-click retargeting when the guess is wrong.

const MAX_QUERIES = 4
const LIMIT = 5
const NAME_IN_TITLE_BONUS = 10
const MAX_ALTERNATIVES = 2
const MAX_QUERY_LEN = 120

const normalize = (s) =>
    String(s || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()

//segments that are site furniture, not tool names — dropping them keeps
//the community host's own brand from outscoring the actual subject
const BOILERPLATE = new Set([
    'forum', 'forums', 'community', 'communities', 'discussion', 'discussions',
    'thread', 'threads', 'comments', 'official', 'news', 'blog', 'blogs',
    'review', 'reviews', 'home', 'index', 'www', 'amp', 'm',
])

//strips handle/tag noise inside a segment: r/sub, u/user, @handle, #tag
const cleanSegment = (s) =>
    String(s || '')
        .replace(/\b[ru]\/\w+/g, ' ')
        .replace(/[@#]\S+/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim()

//hostname labels double as site-brand candidates: community.notion.so →
//['community', 'notion'], appsumo.com → ['appsumo']. The last label (tld)
//is dropped; the rest match a segment only on a full normalized equality
const hostnameLabels = (url) => {
    try {
        const parts = new URL(url).hostname.split('.')
        return parts.filter((l, i) => l !== 'www' && i < parts.length - 1)
    } catch(e) {
        return []
    }
}

//split on title separators: bare | – — · • » « ‹ › (spaced or not) and
//spaced - or : — "ClickUp vs Asana - Reddit" → ['ClickUp vs Asana','Reddit']
const splitTitle = (title) =>
    String(title || '').split(/\s*[|–—·•»«‹›]\s*|\s+[-:]\s+/)

export function fuzzyQueries(title, url) {
    const brands = new Set(hostnameLabels(url).map(normalize))
    const segments = splitTitle(title)
        .map(cleanSegment)
        .filter(s => s.length >= 3)
        .filter(s => {
            const n = normalize(s)
            return n && !brands.has(n) && !BOILERPLATE.has(n)
        })

    const seen = new Set()
    const queries = []
    const push = (q) => {
        const key = normalize(q)
        if (!key || seen.has(key)) return
        seen.add(key)
        queries.push(q.slice(0, MAX_QUERY_LEN))
    }

    //longest segments first — the tool name lives there more often than
    //in short tails like "Question" or "Help"
    segments.slice().sort((a, b) => b.length - a.length)
        .slice(0, MAX_QUERIES - 1)
        .forEach(push)
    //broad fallback built from the RETAINED segments only — joining the
    //raw title would smuggle stripped site brands back into the query
    //(and their names would even earn the title-match bonus downstream)
    push(segments.join(' '))
    return queries.slice(0, MAX_QUERIES)
}

//scores tools across every query's result list: position weight per hit
//(a #1 hit beats a #5), plus a strong bonus when the tool's name/slug
//appears as whole words in the page title. hitsByQuery = array of
//tool arrays in query order. `url` supplies the hostname-brand set so a
//tool matching the forum's own name can't collect the title bonus.
export function rankHits(hitsByQuery, title, url) {
    const paddedTitle = ` ${normalize(title)} `
    const brands = new Set(hostnameLabels(url).map(normalize))
    const scores = new Map() //slug → {tool, score}

    hitsByQuery.forEach((tools) => {
        (tools || []).forEach((tool, i) => {
            if (!tool?.slug) return
            const cur = scores.get(tool.slug) || { tool, score: 0 }
            cur.score += Math.max(LIMIT - i, 0)
            scores.set(tool.slug, cur)
        })
    })

    for (const { tool } of scores.values()) {
        const name = normalize(tool.name)
        const slug = normalize(tool.slug)
        //whole-word match (padded) — 'flow' must not hit inside 'workflow';
        //brand names ('reddit' on reddit.com) can't claim the bonus or the
        //host would outscore the tool actually being discussed
        const hit = [name, slug].some(n =>
            n.length >= 4 && !brands.has(n) && paddedTitle.includes(` ${n} `))
        if (hit) scores.get(tool.slug).score += NAME_IN_TITLE_BONUS
    }

    return [...scores.values()]
        .sort((a, b) => b.score - a.score)
        .map(({ tool }) => tool)
}

//multi-query fuzzy: pages that miss URL matching search LTDHunt by
//title-derived candidates. Returns the best guess marked `guessed: true`
//plus `alternatives` (runner-ups) — the side panel offers them for
//one-click retargeting, which is what makes wrong guesses cheap.
export async function resolveChatTargetFuzzy(title, url) {
    const queries = fuzzyQueries(title, url)
    if (!queries.length) return null
    try {
        const hits = await Promise.all(
            queries.map(q => searchTools(q, LIMIT).catch(() => []))
        )
        const ranked = rankHits(hits, title, url)
        if (!ranked.length) return null

        const toTarget = (tool) => ({
            toolSlug: tool.slug,
            toolName: tool.name,
            toolLogo: tool.logo_url,
            toolId: tool.id,
            type: 'tool',
            guessed: true,
        })
        return {
            ...toTarget(ranked[0]),
            alternatives: ranked.slice(1, 1 + MAX_ALTERNATIVES).map(toTarget),
        }
    } catch(e) {
        return null
    }
}
